import { Fragment } from 'react';
import { convertMinutesToHoursAndMinutes } from '../../utils/dates';

const UnitsNdvi = (value) => {
  return value !== null && value !== undefined ? (value === 0 ? 0 : value.toFixed(4)) : null;
};

const UnitsTemperature = (value) => {
  return value || value === 0 ? `${value > 0 ? '+' : ''}${value} °C` : null;
};

const UnitsTemperatureArea = (arr) => {
  return arr && arr.length > 0 ? `${arr[0] > 0 ? '+' : ''}${arr[0]}...${arr[1] > 0 ? '+' : ''}${arr[1]} °C` : null;
};

const UnitsBbch = (value) => {
  return value || value === 0 ? `${value} %` : null;
};

const ChartTooltip = ({ label, sensors, data, chartsMap, charts, t }) => {
  const NoData = () => <span className="text-muted">{t('empty.noData')}</span>;
  return (
    <div className="chart-tooltip chart-tooltip--large">
      {/*Date*/}
      <div className="chart-tooltip__header">{label}</div>
      {/*Values*/}
      {charts.map((id, index) => {
        let dataValue = data.value;
        let dataValueCompared = data.valueCompared;
        let dataArea = data.area;
        let dataAreaCompared = data.areaCompared;
        let text = data.text;
        let textCompared = data.textCompared;

        if (index === 1) {
          dataValue = data.value2;
          dataArea = data.area2;
        }

        let value;
        let valueCompared;
        let area;
        let areaCompared;

        switch (id) {
          case chartsMap.ndvi.id:
            value = UnitsNdvi(dataValue);
            break;
          case chartsMap.temperature.id:
            value = UnitsTemperature(dataValue);
            area = UnitsTemperatureArea(dataArea);
            break;
          case chartsMap.dew.id:
            value = dataValue || dataValue === 0 ? convertMinutesToHoursAndMinutes(dataValue) : null;
            break;
          case chartsMap.bbch.id:
            value = UnitsBbch(dataValue);
            break;
          default:
            value = dataValue !== null ? `${dataValue} ${chartsMap[id].units ? chartsMap[id].units : ''}` : null;
            area = dataArea && dataArea.length > 0 ? `${dataArea[0]}...${dataArea[1]}` : null;
            break;
        }

        if (sensors.length > 1) {
          switch (id) {
            case chartsMap.ndvi.id:
              valueCompared = UnitsNdvi(dataValueCompared);
              break;
            case chartsMap.temperature.id:
              valueCompared = UnitsTemperature(dataValueCompared);
              areaCompared = UnitsTemperatureArea(dataAreaCompared);
              break;
            case chartsMap.dew.id:
              valueCompared =
                dataValueCompared || dataValueCompared === 0
                  ? convertMinutesToHoursAndMinutes(dataValueCompared)
                  : null;
              break;
            case chartsMap.bbch.id:
              valueCompared = UnitsBbch(dataValueCompared);
              break;
            default:
              valueCompared =
                dataValueCompared || dataValueCompared === 0
                  ? `${dataValueCompared} ${chartsMap[id].units ? chartsMap[id].units : ''}`
                  : null;
              areaCompared =
                dataAreaCompared && dataAreaCompared.length > 0
                  ? `${dataAreaCompared[0]}...${dataAreaCompared[1]}`
                  : null;
              break;
          }
        }

        return sensors.length === 1 ? (
          // 1 sensor, 1 metric
          <Fragment key={id}>
            <div className="chart-tooltip__row">
              <div
                className="chart-tooltip__title"
                style={{ color: index === 0 ? chartsMap[id].color : chartsMap[id].colorCompared }}
              >
                <span>{chartsMap[id].title}</span>
                {area && <span>min/max</span>}
              </div>
              <div className="chart-tooltip__value">
                {value !== null ? <span>{value}</span> : <NoData />}
                {area && <span>{area}</span>}
              </div>
            </div>
            {text && <div className="chart-tooltip__text">{text}</div>}
          </Fragment>
        ) : charts.length === 1 ? (
          // 2 sensors, 1 metric
          <Fragment key={id}>
            <div className="chart-tooltip__row">
              <div
                className="chart-tooltip__title chart-tooltip__title--compared"
                style={{ color: chartsMap[id].color }}
              >
                <span>{sensors[0]}</span>
                {area && <span>min/max</span>}
              </div>
              <div className="chart-tooltip__value">
                {value !== null ? <span>{value}</span> : <NoData />}
                {area && <span>{area}</span>}
              </div>
            </div>
            {text && <div className="chart-tooltip__text">{text}</div>}
            <div className="chart-tooltip__row">
              <div
                className="chart-tooltip__title chart-tooltip__title--compared-secondary"
                style={{ color: chartsMap[id].color }}
              >
                <span>{sensors[1]}</span>
                {areaCompared && <span>min/max</span>}
              </div>
              <div className="chart-tooltip__value">
                {valueCompared ? <span>{valueCompared}</span> : <NoData />}
                {areaCompared && <span>{areaCompared}</span>}
              </div>
            </div>
            {textCompared && <div className="chart-tooltip__text">{textCompared}</div>}
          </Fragment>
        ) : (
          // 2 sensors, 2 metrics
          <div className="chart-tooltip__panel" key={id}>
            {/*metric 1*/}
            <div className="chart-tooltip__panel-title">{chartsMap[id].title}</div>
            <div className="chart-tooltip__row">
              <div
                className="chart-tooltip__title chart-tooltip__title--compared"
                style={{ color: index === 0 ? chartsMap[id].color : chartsMap[id].colorCompared }}
              >
                <span>{sensors[0]}</span>
                {area && <span>min/max</span>}
              </div>
              <div className="chart-tooltip__value">
                {value !== null ? <span>{value}</span> : <NoData />}
                {area && <span>{area}</span>}
              </div>
            </div>
            {text && <div className="chart-tooltip__text">{text}</div>}
            <div className="chart-tooltip__row">
              <div
                className="chart-tooltip__title chart-tooltip__title--compared-secondary"
                style={{ color: index === 0 ? chartsMap[id].color : chartsMap[id].colorCompared }}
              >
                <span>{sensors[1]}</span>
                {areaCompared && <span>min/max</span>}
              </div>
              <div className="chart-tooltip__value">
                {valueCompared !== null ? <span>{valueCompared}</span> : <NoData />}
                {areaCompared && <span>{areaCompared}</span>}
              </div>
            </div>
            {textCompared && <div className="chart-tooltip__text">{textCompared}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default ChartTooltip;
